import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  message,
  Spin,
  Card,
  Divider,
  Col,
  Row,
  Switch,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import TinyEditor from "../../components/Editor";
import { SEO_CONFIG } from "../../seo-congig";

const { TextArea } = Input;

const AddCategoryPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState("");
  const [fetchingData, setFetchingData] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setFetchingData(true);
    try {
      const [categoriesRes] = await Promise.all([
        api.get(`/panel/category?per_page=15`),
      ]);
      setCategories(categoriesRes.data.data || []);
    } catch (error) {
      message.error("دریافت اطلاعات اولیه با خطا مواجه شد");
      console.error("خطا در دریافت اطلاعات:", error);
    } finally {
      setFetchingData(false);
    }
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    try {
      const response = await api.get(`/panel/category`, {
        params: {
          search: value,
          per_page: 15,
        },
      });
      setCategories(response.data.data || []);
    } catch (error) {
      message.error("خطا در جستجوی دسته‌بندی‌ها");
      console.error("خطا در جستجو:", error);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const appendIfExists = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("body", editorContent);
      formData.append("slug", values.slug);
      formData.append("type", values.categoryType);

      // SEO Fields
      appendIfExists("seo_title", values.seo_title);
      appendIfExists("seo_description", values.seo_description);
      appendIfExists("canonical", values.canonical);
      formData.append("follow", values.follow ? "1" : "0");
      formData.append("index", values.index ? "1" : "0");

      if (values.banner?.file) {
        formData.append("image", values.banner.file);
      }

      if (values.categories && values.categories.length > 0) {
        values.categories.forEach((categoryId) =>
          formData.append("sub_categories[]", categoryId)
        );
      }

      const response = await api.post("/panel/category", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("دسته‌بندی با موفقیت اضافه شد.");
      navigate("/categories");
    } catch (error) {
      message.error("خطا در ارسال اطلاعات.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div>
        <h2 className="mb-10 text-xl">افزودن دسته‌بندی جدید</h2>
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={() => {
              message.error("لطفاً فیلدهای ضروری را پر کنید.");
            }}
            scrollToFirstError
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label="عنوان دسته بندی"
              rules={[
                {
                  required: true,
                  message: "لطفاً عنوان را وارد کنید.",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="slug"
              label="پیوند(لینک این  دسته بندی در وبسایت)"
              rules={[
                {
                  required: true,
                  message: "لطفاً پیوند را وارد کنید.",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="categories"
                  label="دسته‌بندی‌ها"
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "لطفاً حداقل یک دسته‌بندی را انتخاب کنید",
                  //   },
                  // ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="دسته‌بندی‌ها را انتخاب کنید"
                    allowClear
                    showSearch
                    filterOption={false} // غیرفعال کردن فیلتر داخلی
                    onSearch={handleSearch} // ارسال درخواست جستجو به API
                    options={categories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="categoryType"
                  label="نوع دسته بندی"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً نوع دسته بندی را انتخاب کنید.",
                    },
                  ]}
                >
                  <Select placeholder="نوع دسته بندی را انتخاب کنید">
                    <Select.Option value="0">محصول</Select.Option>
                    <Select.Option value="1">خبر</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="محتوا"
              required
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!editorContent) {
                      return Promise.reject("لطفاً محتوا را وارد کنید.");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TinyEditor
                content={editorContent}
                onEditorChange={handleEditorChange}
                model={"Category"}
                height={300}
              />
            </Form.Item>

            <div className="border p-4 rounded-lg mb-4">
              <h3 className="text-xl text-blue-500 text-center mb-4">Media</h3>

              <Form.Item
                name="banner"
                label="تصویر بنر"
                // rules={[
                //   {
                //     required: true,
                //     message: "لطفاً تصویر بنر را آپلود کنید.",
                //   },
                // ]}
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    آپلود تصویر
                  </Button>
                </Upload>
              </Form.Item>
            </div>

            <Divider>بخش سئو</Divider>
            <Form.Item
              name="seo_title"
              label="عنوان سئو"
              help={SEO_CONFIG.title.helpText}
            >
              <Input maxLength={SEO_CONFIG.title.maxLength} showCount />
            </Form.Item>

            <Form.Item
              name="seo_description"
              label="توضیحات سئو"
              help={SEO_CONFIG.description.helpText}
            >
              <TextArea
                rows={4}
                maxLength={SEO_CONFIG.description.maxLength}
                showCount
              />
            </Form.Item>

            <Form.Item name="canonical" label="URL Canonical">
              <Input />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="follow" label="Follow" valuePropName="checked">
                  <Switch defaultChecked="true" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="index" label="Index" valuePropName="checked">
                  <Switch defaultChecked="true" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                ثبت
              </Button>
              <Button
                onClick={() => navigate("/categories")}
                style={{ marginRight: "8px" }}
              >
                انصراف
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default AddCategoryPage;
